import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { type GovernanceUnitNode } from "~/api/types";
import { updateGovernanceUnit } from "~/api/v2/requests";
import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import {
  type NewParentOptions,
  newParentOptions,
} from "~/helpers/governanceUnit";
import { GovernanceUnitSelect } from "~/molecules/GovernanceUnitSelect";

interface FormValues {
  parentGovernanceUnitId: number;
}

interface Props {
  governanceUnits: GovernanceUnitNode[];
  governanceUnit: GovernanceUnitNode | null;
  onClose: () => void;
  onMoved: () => void;
}

export function MoveGovernanceUnitDialog({
  governanceUnits,
  governanceUnit,
  onClose,
  onMoved,
}: Props) {
  const { t } = useTranslation();
  const parentOptions =
    governanceUnit && newParentOptions(governanceUnits, governanceUnit);

  return (
    <Dialog open={governanceUnit !== null} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(($) => $.governanceUnits.moveDialog.title)}
          </DialogTitle>
        </DialogHeader>
        {parentOptions && (
          <MoveForm
            key={governanceUnit.id}
            governanceUnit={governanceUnit}
            parentOptions={parentOptions}
            onMoved={() => {
              onMoved();
              onClose();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface MoveFormProps {
  governanceUnit: GovernanceUnitNode;
  parentOptions: NewParentOptions;
  onMoved: () => void;
}

function MoveForm({ governanceUnit, parentOptions, onMoved }: MoveFormProps) {
  const { t } = useTranslation();
  const form = useForm<FormValues>({
    defaultValues: { parentGovernanceUnitId: parentOptions.currentParent.id },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await updateGovernanceUnit(governanceUnit.id, data);
      toast.success(t(($) => $.governanceUnits.moveSuccess));
      onMoved();
    } catch {
      toast.error(t(($) => $.governanceUnits.moveError));
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="parentGovernanceUnitId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t(($) => $.governanceUnits.moveDialog.parentLabel)}
              </FormLabel>
              <FormControl>
                <GovernanceUnitSelect
                  root={parentOptions.possibleParents}
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button
            type="submit"
            // The current parent is preselected; moving there changes nothing.
            disabled={!form.formState.isDirty || form.formState.isSubmitting}
          >
            {t(($) => $.governanceUnits.move)}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
